import React, { useState, useMemo } from 'react';
import { Save, ChevronRight, Award, Users } from 'lucide-react';
import { PRICING_DATA } from '../data/pricingData';

const MAX_SLOTS = 20;

const TeacherOverview = ({ user }) => {
  const [classLogs, setClassLogs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeRail, setActiveRail] = useState(null); 
  
  const [formData, setFormData] = useState({ 
    studentName: '', 
    topic: '', 
    homework: '', 
    sessionType: '1:1',
    studentCount: 1 
  });

  // --- 1. CALCULATE EARNINGS (50% Split + Group Count Logic) ---
  const totalEarnings = useMemo(() => {
    return classLogs.reduce((total, log) => {
      const course = PRICING_DATA[log.courseId];
      if (!course) return total;

      const teacherRate1on1 = course.price * 0.5;
      const teacherRateGroup = course.groupPrice * 0.5;

      // Logic: 1:1 pays base rate. Group pays (Rate x Number of Students)
      const payout = log.sessionType === '1:1' 
        ? teacherRate1on1 
        : (teacherRateGroup * log.studentCount);
      
      return total + payout;
    }, 0);
  }, [classLogs]);

  const handleRailClick = (courseId) => {
    setActiveRail(courseId);
    setFormData({ studentName: '', topic: '', homework: '', sessionType: '1:1', studentCount: 1 });
    setIsModalOpen(true);
  };

  const saveClassLog = () => {
    if (!formData.studentName || !formData.topic) return alert("Please fill in class details.");
    
    const newLog = {
      id: Date.now(),
      courseId: activeRail,
      ...formData,
      timestamp: new Date().toISOString()
    };

    setClassLogs([...classLogs, newLog]);
    setIsModalOpen(false);
  };

  const getRailCount = (courseId) => classLogs.filter(l => l.courseId === courseId).length;

  return (
    <div className="bg-slate-50 min-h-screen rounded-xl font-sans pb-12">
      
      {/* HEADER: TOTAL EARNINGS */}
      <div className="bg-slate-900 rounded-xl p-6 mb-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-green-800 rounded-full blur-3xl opacity-20 -mr-16 -mt-16"></div>
        <div className="flex justify-between items-center relative z-10">
          <div>
            <h2 className="text-slate-400 text-xs uppercase tracking-widest font-bold mb-1">Estimated Net Payout</h2>
            <div className="text-5xl font-extrabold text-[#fbbf24] flex items-center gap-2">
              <span className="text-2xl opacity-50">$</span>{totalEarnings.toFixed(2)}
            </div>
          </div>
          <div className="bg-slate-800 p-3 rounded-lg border border-slate-700 text-center">
             <div className="text-xs text-slate-400 uppercase">Classes</div>
             <div className="text-2xl font-bold">{classLogs.length}</div>
          </div>
        </div>
      </div>

      {/* THE RAILS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
        <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
          <Award className="text-indigo-600" /> Professional Track Progress
        </h3>
        
        <div className="space-y-8 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
            {Object.values(PRICING_DATA).map(course => {
            const count = getRailCount(course.id);
            const teacherRate1on1 = (course.price * 0.5).toFixed(2);
            const teacherRateGroup = (course.groupPrice * 0.5).toFixed(2);

            return (
                <div key={course.id} className="relative group">
                <div className="flex justify-between items-end mb-2">
                    <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${course.price >= 40 ? 'bg-black' : course.price >= 35 ? 'bg-pink-500' : 'bg-blue-500'}`}></span>
                    <div>
                        <h4 className="font-bold text-slate-700 text-sm leading-tight">{course.name}</h4>
                        <div className="text-[10px] text-gray-500">
                             Pay: <span className="font-bold text-green-600">${teacherRate1on1} (1:1)</span> or <span className="font-bold text-blue-600">${teacherRateGroup} (per student)</span>
                        </div>
                    </div>
                    </div>
                    <div className="text-xs font-mono font-bold text-slate-400">{count} / {MAX_SLOTS}</div>
                </div>

                <div className="h-10 bg-[#5c4033] rounded relative shadow-inner border-b-2 border-[#3e2b22] flex items-center px-2 overflow-hidden">
                    <div className="absolute top-1/2 left-0 w-full h-0.5 bg-[#d4af37] opacity-50 z-0"></div>
                    <div className="absolute w-full flex justify-between px-4 z-0">
                    {Array.from({ length: MAX_SLOTS }).map((_, i) => (
                        <span key={i} className={`text-[10px] font-mono font-bold ${i + 1 <= count ? 'text-[#d4af37]' : 'text-[#8b6b5d]'}`}>{i + 1}</span>
                    ))}
                    </div>
                    <div 
                    className="absolute top-1 bottom-1 w-8 bg-gradient-to-b from-[#fcd34d] to-[#b45309] rounded shadow-lg z-10 flex items-center justify-center border border-[#78350f] transition-all duration-500"
                    style={{ left: `calc(${(count / MAX_SLOTS) * 100}% - 16px)` }}
                    >
                    <span className="text-[10px] font-bold text-[#78350f]">{count}</span>
                    </div>
                    {count < MAX_SLOTS && (
                    <button onClick={() => handleRailClick(course.id)} className="absolute w-6 h-6 rounded-full bg-white/10 hover:bg-white/30 text-white flex items-center justify-center transition-all z-20 cursor-pointer shadow-sm border border-white/20 hover:scale-110" style={{ left: `calc(${((count + 1) / MAX_SLOTS) * 100}% - 12px)` }}>
                        <ChevronRight size={14} />
                    </button>
                    )}
                </div>
                </div>
            );
            })}
        </div>
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4 border-b pb-2">Log Session</h3>
            
            <div className="space-y-4">
              {/* Session Type */}
              <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Session Type</label>
                  <div className="flex gap-2">
                      <button onClick={() => setFormData({...formData, sessionType: '1:1', studentCount: 1})} className={`flex-1 py-2 rounded font-bold text-sm border ${formData.sessionType === '1:1' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-500'}`}>1:1 Coaching</button>
                      <button onClick={() => setFormData({...formData, sessionType: 'Group', studentCount: 2})} className={`flex-1 py-2 rounded font-bold text-sm border ${formData.sessionType === 'Group' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-500'}`}>Group Class</button>
                  </div>
              </div>

              {/* Student Count (Only for Group) */}
              {formData.sessionType === 'Group' && (
                  <div className="bg-orange-50 p-3 rounded border border-orange-200">
                      <label className="block text-xs font-bold text-orange-800 uppercase mb-1">Number of Students</label>
                      <div className="flex items-center gap-3">
                        <Users size={16} className="text-orange-600"/>
                        <input 
                            type="number" 
                            min="2" 
                            max="10" 
                            value={formData.studentCount}
                            onChange={(e) => setFormData({...formData, studentCount: parseInt(e.target.value) || 2})}
                            className="w-20 border p-1 rounded font-bold text-center"
                        />
                        <span className="text-xs text-orange-700">x ${(PRICING_DATA[activeRail]?.groupPrice * 0.5).toFixed(2)} = <span className="font-bold underline">${((PRICING_DATA[activeRail]?.groupPrice * 0.5) * formData.studentCount).toFixed(2)}</span></span>
                      </div>
                  </div>
              )}

              <input type="text" value={formData.studentName} onChange={e => setFormData({...formData, studentName: e.target.value})} className="w-full border p-2 rounded" placeholder={formData.sessionType === 'Group' ? "Group ID / Name" : "Student Name"} />
              <input type="text" value={formData.topic} onChange={e => setFormData({...formData, topic: e.target.value})} className="w-full border p-2 rounded" placeholder="Unit / Topic" />
              <textarea value={formData.homework} onChange={e => setFormData({...formData, homework: e.target.value})} className="w-full border p-2 rounded h-20" placeholder="Homework Assigned" />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded">Cancel</button>
              <button onClick={saveClassLog} className="px-6 py-2 bg-indigo-600 text-white font-bold rounded shadow flex items-center gap-2"><Save size={16} /> Confirm Pay</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherOverview;